(() => {
  const setupDepositGallery = (gallery) => {
    if (!gallery || gallery.dataset.xdenDepositReady === 'true') return;
    gallery.dataset.xdenDepositReady = 'true';

    const track = gallery.querySelector('[data-deposit-track]');
    const thumbStrip = gallery.querySelector('.deposit-gallery-thumbs');
    const slides = [...gallery.querySelectorAll('[data-deposit-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-deposit-thumb]')];
    if (!track || !slides.length) return;
    let current = 0;
    let scrollFrame = 0;

    const revealThumb = (index) => {
      const thumb = thumbs[index];
      if (!thumb || !thumbStrip) return;
      const stripBox = thumbStrip.getBoundingClientRect();
      const thumbBox = thumb.getBoundingClientRect();
      const thumbLeft = thumbBox.left - stripBox.left + thumbStrip.scrollLeft;
      const centeredLeft = thumbLeft - (thumbStrip.clientWidth - thumb.clientWidth) / 2;
      thumbStrip.scrollTo({ left: centeredLeft, behavior: 'smooth' });
    };

    const setActive = (index) => {
      if (index === current && gallery.dataset.xdenDepositReadyActive === 'true') return;
      current = index;
      gallery.dataset.xdenDepositReadyActive = 'true';
      thumbs.forEach((thumb, thumbIndex) => thumb.setAttribute('aria-pressed', String(thumbIndex === index)));
      revealThumb(index);
    };

    const activate = (index, behavior = 'smooth') => {
      const nextIndex = Math.max(0, Math.min(index, slides.length - 1));
      setActive(nextIndex);
      track.scrollTo({ left: nextIndex * track.clientWidth, behavior });
    };

    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => activate(index)));
    track.addEventListener('scroll', () => {
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(() => {
        const index = Math.max(0, Math.min(Math.round(track.scrollLeft / track.clientWidth), slides.length - 1));
        setActive(index);
      });
    }, { passive: true });

    gallery.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        activate(current + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });

    if (slides.length > 1) {
      track.classList.add('is-draggable');
      let dragStartX = 0;
      let dragStartScroll = 0;
      let dragging = false;
      track.addEventListener('dragstart', (event) => event.preventDefault());
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragging = true;
        dragStartX = event.clientX;
        dragStartScroll = track.scrollLeft;
        track.classList.add('is-dragging');
        track.setPointerCapture(event.pointerId);
      });
      track.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        track.scrollLeft = dragStartScroll - (event.clientX - dragStartX);
      });
      const stopDrag = () => {
        if (!dragging) return;
        dragging = false;
        track.classList.remove('is-dragging');
        activate(Math.round(track.scrollLeft / track.clientWidth));
      };
      track.addEventListener('pointerup', stopDrag);
      track.addEventListener('pointercancel', stopDrag);
    }

    gallery.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest('[data-deposit-slide]');
      if (slide) activate(slides.indexOf(slide), 'auto');
    });
    setActive(0);
  };

  const setupDepositProductForm = (container) => {
    if (!container || container.dataset.xdenDepositFormReady === 'true') return;
    container.dataset.xdenDepositFormReady = 'true';

    const form = container.querySelector('form');
    if (!form) return;
    const variantId = Number(container.dataset.depositVariantId);
    const cartJsonUrl = container.dataset.cartJsonUrl;
    const cartAddUrl = container.dataset.cartAddUrl;
    const rootUrl = window.Shopify?.routes?.root || '/';
    const cartChangeUrl = `${rootUrl}cart/change.js`;
    const checkoutUrl = `${rootUrl}checkout`;
    const button = form.querySelector('[type="submit"]');
    const message = form.querySelector('[data-deposit-product-message]');
    const defaultLabel = button?.textContent.trim() || 'Reserve My XDen — $50';

    const showMessage = (copy) => {
      if (!message) return;
      message.textContent = copy;
      message.hidden = false;
    };

    const getCart = async () => {
      const response = await fetch(cartJsonUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart could not be checked.');
      return response.json();
    };

    const depositItems = (cart) => cart.items?.filter((item) => Number(item.variant_id) === variantId) || [];

    const removeExistingDeposits = async (cart) => {
      for (const item of depositItems(cart)) {
        const response = await fetch(cartChangeUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
        if (!response.ok) throw new Error('Existing deposit could not be reset.');
      }
    };

    const startCheckout = async (trigger = button) => {
      if (!button || container.dataset.xdenDepositCheckoutBusy === 'true') return;
      container.dataset.xdenDepositCheckoutBusy = 'true';
      const activeTrigger = trigger || button;
      const activeLabel = activeTrigger.textContent.trim() || defaultLabel;
      activeTrigger.setAttribute('aria-busy', 'true');
      if ('disabled' in activeTrigger) activeTrigger.disabled = true;
      activeTrigger.textContent = 'Preparing checkout…';
      if (message) message.hidden = true;

      try {
        const cart = await getCart();
        await removeExistingDeposits(cart);

        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!response.ok) throw new Error('Deposit could not be added to cart.');

        const updatedCart = await getCart();
        const depositQuantity = depositItems(updatedCart).reduce((total, item) => total + item.quantity, 0);
        if (depositQuantity !== 1) throw new Error('Deposit quantity could not be limited to one.');

        window.location.assign(checkoutUrl);
      } catch (error) {
        if ('disabled' in activeTrigger) activeTrigger.disabled = false;
        activeTrigger.removeAttribute('aria-busy');
        activeTrigger.textContent = activeLabel;
        container.dataset.xdenDepositCheckoutBusy = 'false';
        showMessage(error.message || 'Please try again.');
      }
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      startCheckout(event.submitter || button);
    });

    container.addEventListener('xden:deposit-checkout', (event) => {
      startCheckout(event.detail?.trigger || button);
    });
  };

  const setupDepositCheckoutTriggers = () => {
    if (document.documentElement.dataset.xdenDepositCheckoutTriggersReady === 'true') return;
    document.documentElement.dataset.xdenDepositCheckoutTriggersReady = 'true';

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-deposit-checkout-trigger]');
      if (!trigger) return;
      event.preventDefault();

      const container = document.querySelector('[data-deposit-product-form]');
      if (!container) return;
      container.dispatchEvent(new CustomEvent('xden:deposit-checkout', { detail: { trigger } }));
    });
  };

  const init = (root = document) => {
    setupDepositCheckoutTriggers();
    root.querySelectorAll('[data-deposit-gallery]').forEach(setupDepositGallery);
    root.querySelectorAll('[data-deposit-product-form]').forEach(setupDepositProductForm);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
